import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from './DatePicker';
import { ReservationFormPrivateRoomSectionTimeSelect } from './ReservationFormPrivateRoomSectionTimeSelect';
import { ReservationFormPrivateRoomTimeBar } from './ReservationFormPrivateRoomTimeBar';
import type { ReservationFormData } from './ReservationFormServiceTypeSelector';
import type { API } from '@/api';
import type { UseFormSetValue } from 'react-hook-form';
import { RESERVATION_DATE_LIMIT } from '@/constants/reservation';
import { SPACE_TYPE } from '@/constants/space';
import { useDatePicker } from '@/hooks/useDatePicker';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';
import { usePrivateRoomTimeCalculation } from '@/hooks/usePrivateRoomTimeCalculation';
import { type ReservationRestoreData } from '@/hooks/useRestoreReservation';
import { extractTimeFromISO, getDiffMinutes } from '@/utils/datetime';

type PrivateRoomSpaceSlots =
  API['GET']['PrivateRoomAvailableTimeSlots']['response']['spaces'];

interface Props {
  defaultVal: {
    date?: `${number}-${number}-${number}` | null;
    restoreDate?: ReservationRestoreData;
  };
  handleDateSelect: (date: `${number}-${number}-${number}`) => Promise<void>;
  spaces: PrivateRoomSpaceSlots;
  remainingMinutes?: number;
  hasActiveReservation?: boolean;
  setValue: UseFormSetValue<ReservationFormData>;
  setLoungeFloor: (floor: number) => void;
  close: VoidFunction;
  confirmedDateStr: string;
}

export const ReservationFormPrivateRoomSection = ({
  defaultVal,
  handleDateSelect,
  spaces,
  remainingMinutes,
  hasActiveReservation,
  setValue,
  setLoungeFloor,
  close,
  confirmedDateStr,
}: Props) => {
  const { t } = useTranslation();
  const { formatDateWithAvailability } = useDateTimeFormat();
  const { calculateEndTime } = usePrivateRoomTimeCalculation();

  const [date, setDate] = useState(defaultVal.date);

  const [selectedSpaceId, setSelectedSpaceId] = useState<number | undefined>(
    defaultVal.restoreDate?.spaceId,
  );
  const selectedSpace = useMemo(
    () => spaces.find((s) => s.space_id === selectedSpaceId),
    [selectedSpaceId, spaces],
  );

  const [selectedStartHHmm, setSelectedStartHHmm] = useState<
    `${string}:${string}` | null
  >(
    defaultVal.restoreDate?.startDateTime
      ? (extractTimeFromISO(
          defaultVal.restoreDate?.startDateTime,
        ) as `${string}:${string}`)
      : null,
  ); //HH:mm形式

  const [selectedDurationMinutes, setSelectedDuration] = useState<number>(
    defaultVal.restoreDate?.startDateTime && defaultVal.restoreDate.endDateTime
      ? getDiffMinutes(
          defaultVal.restoreDate.startDateTime,
          defaultVal.restoreDate.endDateTime,
        )
      : 0,
  );

  const onDateSelect = async (selectedDate: string) => {
    setDate(selectedDate as `${number}-${number}-${number}`);
    await handleDateSelect(selectedDate as `${number}-${number}-${number}`);
  };

  const { currentMonth, changeMonth, setCurrentMonth } = useDatePicker({
    maxDaysFromToday: RESERVATION_DATE_LIMIT.PRIVATE_ROOM,
    onDateSelect,
  });
  const handleSlotSelect = (spaceId: number, time: `${string}:${string}`) => {
    setSelectedSpaceId(spaceId);
    setSelectedStartHHmm(time);
  };

  const handleOnConfirmed = () => {
    if (!selectedStartHHmm || !selectedDurationMinutes || !selectedSpace)
      return;

    // selectedStartTimeとdurationからendTimeを求める
    const endTime = calculateEndTime(
      selectedStartHHmm,
      selectedDurationMinutes,
    );

    setValue('selectedSpaceId', selectedSpaceId);
    setValue('selectedSpaceNo', selectedSpace.space_no);
    setValue('selectedStartTime', selectedStartHHmm);
    setValue('selectedEndTime', endTime);
    setLoungeFloor(selectedSpace.floor);
    close();
  };

  return (
    <>
      <div className="reservation-card__step-header no-after-content" translate="no">
        <div className="reservation-card__step-label w-full flex justify-between">
          {confirmedDateStr ? (
            <>
              <span>{confirmedDateStr}</span>
              <span>＋</span>
            </>
          ) : (
            <div className="flex justify-between w-full">
              <span>{t('reservation.boarding.dateAndTime')}</span>
              <button
                type="button"
                onClick={() => {
                  setDate(undefined);
                  setSelectedSpaceId(undefined);
                  setSelectedStartHHmm(null);
                  setSelectedDuration(0);
                }}
              >
                <span className="py-5 pr-2 pl-5">−</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="reservation-card__step-body">
        {(!date || hasActiveReservation) && (
          <>
            <div className="reservation-card__datetime">
              <DatePicker
                selectedDate={date}
                onDateSelect={onDateSelect}
                maxDaysFromToday={RESERVATION_DATE_LIMIT.PRIVATE_ROOM}
                spaceType={SPACE_TYPE.PRIVATE_ROOM}
                currentMonth={currentMonth}
                changeMonth={changeMonth}
                setCurrentMonth={setCurrentMonth}
              />
              <input
                type="hidden"
                name="reserve-datetime"
                data-step-input="4"
              />
            </div>
            {/* タイムゾーン注記 */}
            {/* 日付選択画面での表示用 */}
            <div className="reservation-card__info reservation-card__info--timezone">
              {t('reservation.boarding.timezoneNote')}
            </div>
          </>
        )}

        <fieldset className="booth-picker" translate="no">
          <legend className="booth-picker__legend">
            <div className="booth-picker__date">
              {date
                ? formatDateWithAvailability(date)
                : t('reservation.privateRoom.selectDate')}
            </div>
          </legend>
          <div className="booth-picker__body">
            {!date || hasActiveReservation ? null : selectedSpace &&
              selectedStartHHmm ? (
              <>
                <ReservationFormPrivateRoomTimeBar
                  name={selectedSpace.space_name}
                  timeSlots={selectedSpace.time_slots}
                  selectedStartHHmm={selectedStartHHmm}
                  selectedDurationMinutes={selectedDurationMinutes}
                  handleSlotSelect={(time) => {
                    setSelectedStartHHmm(time);
                    setSelectedDuration(0);
                  }}
                />
                {/* タイムゾーン注記 */}
                {/* ブース一選択後の表示用 */}
                <div className="reservation-card__info reservation-card__info--timezone">
                  {t('reservation.boarding.timezoneNote')}
                </div>
                <ReservationFormPrivateRoomSectionTimeSelect
                  timeSlots={selectedSpace.time_slots}
                  startHHMM={selectedStartHHmm}
                  setStartHHMM={setSelectedStartHHmm}
                  durationMinutes={selectedDurationMinutes}
                  setDurationMinutes={setSelectedDuration}
                  remainingMinutes={remainingMinutes}
                />
                <div className="booth-picker__actions">
                  {!!selectedDurationMinutes && (
                    <button
                      className="booth-picker__action booth-picker__action--confirm"
                      onClick={handleOnConfirmed}
                      type="button"
                    >
                      {t('reservation.privateRoom.confirmTime')}
                    </button>
                  )}
                  <button
                    className="booth-picker__action booth-picker__action--back"
                    onClick={() => {
                      setDate(undefined);
                      setSelectedSpaceId(undefined);
                      setSelectedStartHHmm(null);
                      setSelectedDuration(0);
                    }}
                    type="button"
                  >
                    {t('reservation.privateRoom.back')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* タイムゾーン注記 */}
                {/* ブース一覧時の表示用 */}
                <div className="reservation-card__info reservation-card__info--timezone">
                  {t('reservation.boarding.timezoneNote')}
                </div>
                <div className="booth-picker__headline">
                  <img
                    src="/B_2_Icon_Booth.png"
                    alt={t('reservation.privateRoom.selectBooth')}
                    className="booth-picker__headline--icon"
                  />
                  {t('reservation.privateRoom.selectBooth')}
                </div>

                <div className="booth-picker__list">
                  {spaces.map((space) => {
                    const firstAvailableSlot = space.time_slots.find(
                      (slot) => slot.available && slot.displayable,
                    );
                    return (
                      <ReservationFormPrivateRoomTimeBar
                        key={space.space_id}
                        name={space.space_name}
                        timeSlots={space.time_slots}
                        handleSlotSelect={(time) =>
                          handleSlotSelect(space.space_id, time)
                        }
                        onBoothClick={
                          firstAvailableSlot
                            ? () =>
                                handleSlotSelect(
                                  space.space_id,
                                  firstAvailableSlot.time as `${string}:${string}`,
                                )
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </fieldset>
      </div>
    </>
  );
};
